import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { MessageCircle, Gift, Loader2 } from 'lucide-react';
import { discussionService, Discussion as DiscussionType } from '@services/discussionService';
import { useAuthStore } from '@store/authStore';
import { useDiscussionStore } from '@store/discussionStore';
import toast from 'react-hot-toast';
import Header from '@components/layout/Header';
import Footer from '@components/layout/Footer';
import BackgroundLayer from '@components/layout/Background/BackgroundLayer';
import MobileBottomNav from '@components/layout/MobileBottomNav';
import ConfirmationModal from '@components/common/ConfirmationModal';
import DiscussionCard from './components/DiscussionCard';
import DiscussionDetail from './components/DiscussionDetail';
import CreateDiscussionInline from './components/CreateDiscussionInline';
import DiscussionFilters from './components/DiscussionFilters';
import PublicPageHeader from '@components/common/PublicPageHeader';
import { pricing } from '@config/pricing';

const Discussion = () => {
    const { discussionId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { user, isAuthenticated } = useAuthStore();
    const [hasClaimedReward, setHasClaimedReward] = useState(false);
    const [checkingReward, setCheckingReward] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);

    // Get initial state from navigation
    const initialType = location.state?.type;
    const autoExpand = location.state?.autoExpand;

    // Use discussion store for state management
    const {
        discussions,
        selectedDiscussion,
        listLoading: loading,
        detailLoading: loadingDetail,
        currentPage,
        totalPages,
        selectedType,
        hasMore,
        setDiscussions,
        appendDiscussions,
        setSelectedDiscussion,
        setListLoading,
        setDetailLoading,
        setCurrentPage,
        setSelectedType,
        updateDiscussionVote,
        updateDiscussionPin,
    } = useDiscussionStore();

    // Confirmation modal state
    const [deleteConfirmModal, setDeleteConfirmModal] = useState<{
        isOpen: boolean;
        discussionId: string | null;
    }>({ isOpen: false, discussionId: null });

    // Infinite scroll observer ref
    const observerTarget = useRef<HTMLDivElement>(null);

    // Check if user has already claimed the first_discussion reward
    useEffect(() => {
        const checkRewardStatus = async () => {
            if (!user?._id || !isAuthenticated) {
                setCheckingReward(false);
                return;
            }

            try {
                // This uses RewardTracking which persists even if user deletes discussions
                const response = await discussionService.checkRewardClaimed();
                setHasClaimedReward(response.has_claimed);
            } catch (error) {
                console.error('Failed to check reward status:', error);
            } finally {
                setCheckingReward(false);
            }
        };

        checkRewardStatus();
    }, [user, isAuthenticated]);

    // Function definitions (must be before useEffects that use them)
    const loadDiscussions = async () => {
        try {
            setListLoading(true);
            const response = await discussionService.getDiscussions(1, 20, selectedType);
            setDiscussions(response.discussions, response.total_pages);
            setCurrentPage(1);
        } catch (error: any) {
            console.error('Failed to load discussions:', error);
            toast.error('Failed to load discussions');
        } finally {
            setListLoading(false);
        }
    };

    const loadMoreDiscussions = useCallback(async () => {
        // Prevent loading if already loading or no more items
        if (loadingMore || !hasMore) return;

        try {
            setLoadingMore(true);
            const nextPage = currentPage + 1;
            const response = await discussionService.getDiscussions(nextPage, 20, selectedType);

            // Append discussions
            appendDiscussions(response.discussions, nextPage < response.total_pages);
            setCurrentPage(nextPage);
        } catch (error: any) {
            console.error('Failed to load more discussions:', error);
            toast.error('Failed to load more discussions');
        } finally {
            setLoadingMore(false);
        }
    }, [loadingMore, hasMore, currentPage, selectedType, appendDiscussions, setCurrentPage]);

    const loadDiscussionDetail = async (id: string) => {
        try {
            setDetailLoading(true);
            const response = await discussionService.getDiscussion(id);
            setSelectedDiscussion(response.discussion);
        } catch (error: any) {
            console.error('Failed to load discussion:', error);
            toast.error('Discussion not found');
            navigate('/discussion');
        } finally {
            setDetailLoading(false);
        }
    };

    // Clear location state after initial use
    useEffect(() => {
        if (location.state?.autoExpand) {
            // Replace current history entry to clear the state
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, []);

    // Load discussions list (reload from page 1 when type changes)
    useEffect(() => {
        loadDiscussions();
    }, [selectedType]);

    // Load single discussion if ID in URL
    useEffect(() => {
        if (discussionId) {
            loadDiscussionDetail(discussionId);
        } else {
            setSelectedDiscussion(null);
        }
    }, [discussionId]);

    // Infinite scroll: Observe when user scrolls near bottom
    useEffect(() => {
        // Don't set up observer if viewing single discussion
        if (discussionId || selectedDiscussion) return;

        const observer = new IntersectionObserver(
            (entries) => {
                // When the sentinel element becomes visible, load more
                if (entries[0].isIntersecting && hasMore && !loadingMore) {
                    loadMoreDiscussions();
                }
            },
            { threshold: 0.1, rootMargin: '100px' } // Trigger 100px before reaching the element
        );

        if (observerTarget.current) {
            observer.observe(observerTarget.current);
        }

        return () => {
            if (observerTarget.current) {
                observer.unobserve(observerTarget.current);
            }
        };
    }, [discussionId, selectedDiscussion, hasMore, loadingMore, loadMoreDiscussions]);

    const handleDiscussionClick = (discussion: DiscussionType) => {
        navigate(`/discussion/${discussion.id}`);
    };

    const handleBack = () => {
        navigate('/discussion');
        setSelectedDiscussion(null);
    };

    const handleCreateSuccess = (discussionId: string) => {
        loadDiscussions();
        navigate(`/discussion/${discussionId}`);
    };

    const handleTypeChange = (type: string) => {
        // setSelectedType from store already resets page to 1
        setSelectedType(type);
    };

    const handleVote = async (discussionId: string, voteType: 'up' | 'down') => {
        if (!isAuthenticated || !user?._id) {
            toast.error('Please login to vote');
            return;
        }

        try {
            // Call API and get updated counts
            const response = voteType === 'up'
                ? await discussionService.upvoteDiscussion(discussionId)
                : await discussionService.downvoteDiscussion(discussionId);

            // Update store with new counts (optimistic update)
            updateDiscussionVote(
                discussionId,
                user._id,
                voteType,
                response.upvote_count,
                response.downvote_count
            );
        } catch (error: any) {
            toast.error(error.response?.data?.error || 'Failed to vote');
        }
    };

    const handleDelete = (discussionId: string) => {
        setDeleteConfirmModal({ isOpen: true, discussionId });
    };

    const confirmDelete = async () => {
        if (!deleteConfirmModal.discussionId) return;

        try {
            await discussionService.deleteDiscussion(deleteConfirmModal.discussionId);
            toast.success('Discussion deleted');
            // Remove from store without refetching
            useDiscussionStore.getState().removeDiscussion(deleteConfirmModal.discussionId);
            navigate('/discussion');
        } catch (error: any) {
            toast.error(error.response?.data?.error || 'Failed to delete discussion');
        } finally {
            setDeleteConfirmModal({ isOpen: false, discussionId: null });
        }
    };

    const handlePin = async (discussionId: string) => {
        try {
            const response = await discussionService.togglePin(discussionId);
            toast.success(response.action === 'pinned' ? 'Discussion pinned to top' : 'Discussion unpinned');
            // Update store with new pin state
            updateDiscussionPin(discussionId, response.pinned);
        } catch (error: any) {
            toast.error(error.response?.data?.error || 'Failed to toggle pin');
        }
    };

    return (
        <div className="min-h-screen bg-cosmic-950 text-white font-sans selection:bg-primary-900 selection:text-white relative pb-16 md:pb-0">
            <BackgroundLayer />
            <Header />

            <main className="relative z-10 pt-14 md:pt-16 pb-12 md:pb-32 px-4 sm:px-6">
                <div className="max-w-5xl mx-auto">
                    {/* Header Section */}
                    {!selectedDiscussion && !discussionId && (
                        <div className="mb-12 md:mb-20">
                            <PublicPageHeader
                                title="Discussion Hub"
                                highlightedText="Discussion Hub"
                                description="Join our cosmic community: share feedback, ask questions, report bugs, and explore celestial wisdom with fellow seekers."
                                icon={MessageCircle}
                            />

                            <div className="space-y-10 md:space-y-12">
                                {/* Free Credit Banner - Only show if user hasn't claimed reward yet */}
                                {isAuthenticated && !checkingReward && !hasClaimedReward && (
                                    <div className="bg-cosmic-900/40 backdrop-blur-xl border-2 border-dashed border-purple-500/30 rounded-2xl p-4 shadow-lg">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-pink-600 flex items-center justify-center shadow-lg shadow-orange-500/20 shrink-0">
                                                <Gift size={18} className="text-white" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-bold text-white font-sans">Get {pricing.currency}{pricing.discussionReward} Free Credit!</p>
                                                <p className="text-xs text-purple-300/70 font-sans">
                                                    {user?.phone_verified
                                                        ? 'Post your first discussion & earn free questions instantly'
                                                        : 'Verify your phone number in your profile, then post your first discussion to earn'}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Inline discussion input for logged in users */}
                                {isAuthenticated && (
                                    <CreateDiscussionInline
                                        onSuccess={handleCreateSuccess}
                                        initialType={initialType}
                                        autoExpand={autoExpand}
                                        onDiscussionCreated={() => setHasClaimedReward(true)}
                                    />
                                )}

                                <DiscussionFilters
                                    selectedType={selectedType}
                                    onTypeChange={handleTypeChange}
                                />
                            </div>
                        </div>
                    )}

                    {/* Main Content */}
                    {selectedDiscussion || discussionId ? (
                        <DiscussionDetail
                            discussion={selectedDiscussion}
                            loading={loadingDetail}
                            onBack={handleBack}
                            onVote={handleVote}
                            onDelete={handleDelete}
                        />
                    ) : (
                        <>
                            {/* Discussions List */}
                            {loading ? (
                                <div className="space-y-4">
                                    {[1, 2, 3].map((i) => (
                                        <div
                                            key={i}
                                            className="h-48 bg-white/[0.08] rounded-3xl border border-white/20 animate-pulse"
                                        />
                                    ))}
                                </div>
                            ) : discussions.length > 0 ? (
                                <div className="space-y-4">
                                    {discussions.map((discussion) => (
                                        <DiscussionCard
                                            key={discussion.id}
                                            discussion={discussion}
                                            onClick={() => handleDiscussionClick(discussion)}
                                            onVote={handleVote}
                                            onPin={handlePin}
                                            currentUserId={user?._id}
                                        />
                                    ))}

                                    {/* Infinite Scroll Sentinel & Loading Indicator */}
                                    {hasMore && (
                                        <div ref={observerTarget} className="flex justify-center items-center min-h-[80px] py-4">
                                            {loadingMore && (
                                                <div className="flex items-center gap-2 text-purple-400">
                                                    <Loader2 className="w-5 h-5 animate-spin" />
                                                    <span className="text-sm font-sans">Loading more discussions...</span>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="text-center py-20">
                                    <MessageCircle size={64} className="mx-auto text-purple-400/30 mb-4" />
                                    <h3 className="text-xl font-bold text-white mb-2 font-serif">No discussions yet</h3>
                                    <p className="text-purple-300/60 mb-6 font-sans">
                                        {isAuthenticated
                                            ? 'Be the first to start a discussion!'
                                            : 'No discussions in this category yet.'}
                                    </p>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </main>

            <Footer />
            <MobileBottomNav />

            {/* Delete Confirmation Modal */}
            <ConfirmationModal
                isOpen={deleteConfirmModal.isOpen}
                onClose={() => setDeleteConfirmModal({ isOpen: false, discussionId: null })}
                onConfirm={confirmDelete}
                title="Delete Discussion"
                message="Are you sure you want to delete this discussion? This action cannot be undone and will also delete all comments."
                confirmText="Delete"
                cancelText="Cancel"
                variant="danger"
            />
        </div>
    );
};

export default Discussion;
